import { Alert, Badge, Button, Col, Row } from "reactstrap";
import { useState, useMemo } from "react";
import { SelectPicker } from "rsuite";

import { BulkUsersPayload } from "../../api/api-hooks/pages/useBulkPageUsers";
import AppSpinner from "../../elements/AppSpinner";
import AppDrawer from "./AppDrawer";

interface Props {
  open: boolean;
  onClose: () => void;
  selectedMembers: string[];
  permissions: any;
  bulkUpdate: (payload: BulkUsersPayload, onSuccess?: () => void) => void;
  updatingBulkUsers: boolean;
  updateBulkUsersError: string;
  onSuccess?: () => void;
  pagePeople: any[];
}

const BulkdUsersPermissionsDrawer = ({
  open,
  onClose,
  selectedMembers,
  permissions,
  bulkUpdate,
  updatingBulkUsers,
  updateBulkUsersError,
  pagePeople,
  onSuccess = () => {},
}: Props) => {
  const [invalidProfileName, setInvalidProfileName] = useState("");
  const [selectedPermission, setSelectedPermission] = useState<string | null>(
    null
  );

  const onSaveSuccess = () => {
    setSelectedPermission(null);
    onClose();
    onSuccess();
  };

  const members = useMemo(() => {
    setInvalidProfileName("");
    return pagePeople.flatMap((m: any) => {
      if (!selectedMembers.includes(m.id)) return [];

      if (selectedPermission && m.PagePermissions?.id === selectedPermission) {
        m.color = "var(--danger)";
        setInvalidProfileName(m.PagePermissions?.Name);
      } else {
        m.color = "var(--primary)";
      }

      return [m];
    });
  }, [pagePeople, selectedMembers, selectedPermission]);

  const permissionsData = useMemo(() => {
    return permissions.flatMap((p: any) => {
      if (p.Name.includes("Owner")) return [];
      return [
        {
          value: p.id,
          label: p.Name,
          description: p.Description,
        },
      ];
    });
  }, [permissions]);

  const handleSave = () => {
    bulkUpdate(
      {
        UserIDs: selectedMembers,
        Type: "Update",
        PermissionID: selectedPermission!,
      },
      onSaveSuccess
    );
  };

  return (
    <AppDrawer
      open={open}
      onClose={onClose}
      headerTitle={`Edit Permissions`}
      headerOptions={
        <Button
          size="sm"
          color="primary"
          onClick={handleSave}
          disabled={updatingBulkUsers || !!invalidProfileName}
          style={{ width: 80 }}
        >
          {updatingBulkUsers ? <AppSpinner color="white" /> : "Save"}
        </Button>
      }
      body={
        <>
          {invalidProfileName && (
            <Alert color="danger" className="mb-4">
              You can't assign the same permissions, some users are already a{" "}
              {invalidProfileName}
            </Alert>
          )}
          {updateBulkUsersError && (
            <Alert color="danger" className="mb-4">
              {updateBulkUsersError}
            </Alert>
          )}
          <h4 className="mb-4 ">
            Editing Permissions for:
            {members.map((member) => {
              return (
                <Row
                  className="d-flex align-items-center pt-3"
                  key={member?.id}
                >
                  <Col xs="auto">
                    <span
                      style={{
                        color: member.color,
                        cursor: "pointer",
                        marginLeft: 4,
                      }}
                    >
                      {member?.Name || member?.PrimaryContactEmail}
                    </span>
                  </Col>
                  <Col xs="auto" className="text-right">
                    <Badge
                      color={
                        member.PagePermissions.Owner ? "secondary" : "light"
                      }
                    >
                      {member.PagePermissions.Name}
                    </Badge>
                  </Col>
                </Row>
              );
            })}
          </h4>
          <SelectPicker
            style={{ width: "100%" }}
            preventOverflow
            data={permissionsData}
            placeholder="Assign Permissions"
            renderMenuItem={(label, item) => {
              return (
                <div style={{ maxWidth: 515 }}>
                  <h4 className="mb-0">{label}</h4>
                  <small>{item.description}</small>
                </div>
              );
            }}
            onSelect={(_, item) => {
              //@ts-ignore
              setSelectedPermission(item.value);
            }}
            value={selectedPermission}
          />
        </>
      }
    />
  );
};

export default BulkdUsersPermissionsDrawer;
